// Message catalogs for the venue dashboard.
//
// Mexican Spanish is the default, not English. Every venue on this platform is
// in Mexico, the deployment's own default locale is es-MX, and the guest and
// waiter apps have been Spanish-first from the start — a manager opening the
// dashboard in English was the odd one out.
//
// The wording follows the vocabulary the waiter app already settled on
// (`packages/i18n/src/locales/es-MX.ts`): a tip is a *propina*, money owed to
// someone is their *saldo*, and money reaching them is a *depósito*. Inventing
// a second vocabulary for the same concepts one screen away is how a product
// starts sounding like two products.
type Catalog = Record<string, string>;

const catalogs: Record<string, Catalog> = {
  "es-MX": {
    "app.title": "Panel de Mana",
    "login.email": "Correo",
    "login.password": "Contraseña",
    "login.submit": "Entrar",
    "login.error": "Correo o contraseña incorrectos.",
    "nav.recipients": "Beneficiarios",
    "nav.tip_pages": "Páginas de propinas",
    "nav.payouts": "Depósitos",
    "nav.gamification": "Gamificación",
    "nav.stats": "Resumen",
    "nav.logout": "Salir",
    "recipients.name": "Nombre",
    "recipients.kind": "Tipo",
    "recipients.add": "Agregar beneficiario",
    "recipients.id": "ID",
    "tippages.recipient": "ID del beneficiario",
    "tippages.currency": "Moneda",
    "tippages.presets": "Montos sugeridos (separados por comas, en centavos)",
    // The greeting the guest reads on the tipping page. Spanish comes first
    // because it is the one they will actually see.
    "tippages.greeting_es": "Saludo (español)",
    "tippages.greeting_en": "Saludo (inglés)",
    "tippages.greeting_ru": "Saludo (ruso)",
    "tippages.add": "Crear página",
    "tippages.links": "Enlaces / QR",
    "tippages.slug": "Dirección",
    "tippages.presets_short": "Montos",
    "payouts.recipient": "ID del beneficiario",
    "payouts.currency": "Moneda",
    "payouts.trigger": "Depositar saldo",
    "payouts.none": "No hay saldo por depositar.",
    "game.badge_code": "Código de insignia",
    "game.metric": "Métrica",
    "game.threshold": "Umbral",
    "game.add_badge": "Crear insignia",
    "stats.tips": "Propinas",
    "stats.outstanding": "Saldos por depositar",
    "stats.count": "Núm.",
    "stats.gross": "Bruto",
    "stats.fees": "Comisiones",
    "common.create": "Crear",
    "common.error": "Algo salió mal.",
    "common.empty": "Todavía no hay nada.",
    "login.too_many": "Demasiados intentos. Espera unos minutos.",
    "common.cancel": "Cancelar",
    // Backoffice: the Mana team's own screens (platform admin login).
    "bo.title": "Backoffice de Mana",
    "bo.nav.codes": "Códigos",
    "bo.nav.venues": "Establecimientos",
    "bo.nav.staff": "Registrados",
    "bo.issue.title": "Emitir código de invitación",
    "bo.issue.venue": "Establecimiento",
    "bo.issue.new_venue": "— Establecimiento nuevo —",
    "bo.issue.venue_name": "Nombre del establecimiento",
    "bo.issue.neighborhood": "Colonia",
    "bo.issue.code": "Código",
    "bo.issue.code_placeholder": "Vacío = aleatorio",
    "bo.issue.code_hint": "6 caracteres de A–Z y 2–9, sin O, 0, I ni 1: la pantalla de registro los descarta.",
    "bo.issue.max_uses": "Máximo de registros",
    "bo.issue.no_limit": "Sin límite",
    "bo.issue.submit": "Emitir código",
    "bo.issue.venue_created": "(establecimiento nuevo)",
    "bo.issue.existing": "{code} ya abre {venue}; no cambió nada.",
    "bo.issue.tell": "Dáselo solo a quien trabaja ahí: sus clientes leerán «{name}» en la pantalla de propina.",
    "bo.issue.hood_ignored": "El establecimiento ya existía con la colonia «{hood}»; se dejó igual.",
    "bo.codes.code": "Código",
    "bo.codes.venue": "Establecimiento",
    "bo.codes.signups": "Registros",
    "bo.codes.status": "Estado",
    "bo.codes.issued": "Emitido",
    "bo.codes.empty": "Todavía no hay códigos. Emite el primero arriba.",
    "bo.codes.show_staff": "Ver quién se registró con este código",
    "bo.codes.revoke": "Revocar",
    "bo.codes.revoke_confirm": "Sí, revocar",
    "bo.codes.revoke_warn": "¿Revocar {code}? Nadie nuevo podrá registrarse con él, y no se vuelve a emitir nunca. Quien ya se registró conserva su cuenta y su QR.",
    "bo.status.active": "Activo",
    "bo.status.revoked": "Revocado",
    "bo.status.expired": "Vencido",
    "bo.status.full": "Agotado",
    "bo.status.personal": "personal",
    "bo.venues.hint": "Agrega un establecimiento aquí o al emitir su primer código. Su nombre es lo que leen los clientes en la pantalla de propina. Solo se puede eliminar mientras nadie trabaje ahí y no tenga mesas ni propinas.",
    "bo.venues.add_title": "Nuevo establecimiento",
    "bo.venues.add": "Agregar",
    "bo.venues.added": "Listo: {venue}. Emítele un código para que su gente pueda registrarse.",
    "bo.venues.issue": "Emitir código",
    "bo.venues.delete": "Eliminar",
    "bo.venues.delete_confirm": "Sí, eliminar",
    "bo.venues.delete_warn": "¿Eliminar {name}? No se puede deshacer.",
    "bo.venues.delete_warn_codes": "¿Eliminar {name}? Sus códigos sin usar ({codes}) dejan de funcionar. No se puede deshacer.",
    "bo.venues.deleted": "{name} eliminado.",
    "bo.venues.name": "Nombre",
    "bo.venues.neighborhood": "Colonia",
    "bo.venues.codes": "Códigos vigentes",
    "bo.venues.staff": "Meseros",
    "bo.venues.created": "Creado",
    "bo.venues.edit": "Editar",
    "bo.venues.save": "Guardar",
    "bo.venues.protected": "Revisión de tiendas · lo maneja seedreview",
    "bo.venues.rename_note": "Los clientes verán el nombre nuevo desde el próximo escaneo, también en los QR ya impresos.",
    "bo.staff.name": "Nombre",
    "bo.staff.email": "Correo",
    "bo.staff.venue": "Establecimiento",
    "bo.staff.code": "Código",
    "bo.staff.status": "Estado",
    "bo.staff.signed_up": "Registro",
    "bo.staff.all_venues": "Todos los establecimientos",
    "bo.staff.search": "Buscar nombre o correo",
    "bo.staff.code_filter": "Código {code}",
    "bo.staff.code_unknown": "Registrado antes de que se guardara el código",
    "bo.staff.count": "{n} personas",
    "bo.staff.count_one": "1 persona",
    "bo.staff.status.confirmed": "Confirmado",
    "bo.staff.status.pending": "Sin confirmar",
    "bo.staff.status.closed": "Cuenta cerrada",
    "bo.err.code_shape": "El código debe tener 6 caracteres de A–Z y 2–9, sin O, 0, I ni 1.",
    "bo.err.code_taken": "Ese código ya abre otro establecimiento. Elige otro.",
    "bo.err.code_revoked": "Ese código fue revocado y no se vuelve a emitir. Elige otro.",
    "bo.err.code_personal": "Ese es el código personal de un mesero. Elige otro.",
    "bo.err.code_not_found": "Ese código no existe.",
    "bo.err.venue_required": "Escribe el nombre del establecimiento.",
    "bo.err.venue_too_long": "El nombre es demasiado largo (máximo 80 caracteres).",
    "bo.err.neighborhood_too_long": "La colonia es demasiado larga (máximo 80 caracteres).",
    "bo.err.venue_protected": "Ese es el establecimiento de revisión de las tiendas; se maneja con seedreview.",
    "bo.err.venue_name_taken": "Ya hay un establecimiento con ese nombre.",
    "bo.err.venue_ambiguous": "Hay varios establecimientos con ese nombre; renombra uno primero.",
    "bo.err.venue_not_found": "Ese establecimiento ya no existe.",
    "bo.err.max_uses_negative": "El máximo no puede ser negativo.",
    "bo.err.venue_in_use": "Este establecimiento tiene personas, mesas o propinas, así que no se puede eliminar. Cámbiale el nombre o revoca sus códigos.",
  },
  en: {
    "app.title": "Mana Dashboard",
    "login.email": "Email",
    "login.password": "Password",
    "login.submit": "Sign in",
    "login.error": "Invalid email or password.",
    "nav.recipients": "Recipients",
    "nav.tip_pages": "Tip pages",
    "nav.payouts": "Deposits",
    "nav.gamification": "Gamification",
    "nav.stats": "Summary",
    "nav.logout": "Sign out",
    "recipients.name": "Display name",
    "recipients.kind": "Kind",
    "recipients.add": "Add recipient",
    "recipients.id": "ID",
    "tippages.recipient": "Recipient ID",
    "tippages.currency": "Currency",
    "tippages.presets": "Preset amounts (comma-separated, in minor units)",
    "tippages.greeting_es": "Greeting (Spanish)",
    "tippages.greeting_en": "Greeting (English)",
    "tippages.greeting_ru": "Greeting (Russian)",
    "tippages.add": "Create tip page",
    "tippages.links": "Links / QR",
    "tippages.slug": "Address",
    "tippages.presets_short": "Amounts",
    "payouts.recipient": "Recipient ID",
    "payouts.currency": "Currency",
    "payouts.trigger": "Deposit balance",
    "payouts.none": "No balance to deposit.",
    "game.badge_code": "Badge code",
    "game.metric": "Metric",
    "game.threshold": "Threshold",
    "game.add_badge": "Create badge",
    "stats.tips": "Tips",
    "stats.outstanding": "Balances to deposit",
    "stats.count": "Count",
    "stats.gross": "Gross",
    "stats.fees": "Fees",
    "common.create": "Create",
    "common.error": "Something went wrong.",
    "common.empty": "Nothing yet.",
    "login.too_many": "Too many attempts. Wait a few minutes.",
    "common.cancel": "Cancel",
    "bo.title": "Mana Backoffice",
    "bo.nav.codes": "Codes",
    "bo.nav.venues": "Venues",
    "bo.nav.staff": "Signed up",
    "bo.issue.title": "Issue an invitation code",
    "bo.issue.venue": "Venue",
    "bo.issue.new_venue": "— New venue —",
    "bo.issue.venue_name": "Venue name",
    "bo.issue.neighborhood": "Neighborhood",
    "bo.issue.code": "Code",
    "bo.issue.code_placeholder": "Empty = random",
    "bo.issue.code_hint": "6 characters from A–Z and 2–9, no O, 0, I or 1: the sign-up screen drops those.",
    "bo.issue.max_uses": "Max sign-ups",
    "bo.issue.no_limit": "No limit",
    "bo.issue.submit": "Issue code",
    "bo.issue.venue_created": "(new venue)",
    "bo.issue.existing": "{code} already opens {venue}; nothing changed.",
    "bo.issue.tell": "Give it only to people who work there: their guests will read “{name}” on the tip screen.",
    "bo.issue.hood_ignored": "The venue already existed with neighborhood “{hood}”; left as it was.",
    "bo.codes.code": "Code",
    "bo.codes.venue": "Venue",
    "bo.codes.signups": "Sign-ups",
    "bo.codes.status": "Status",
    "bo.codes.issued": "Issued",
    "bo.codes.empty": "No codes yet. Issue the first one above.",
    "bo.codes.show_staff": "See who signed up with this code",
    "bo.codes.revoke": "Revoke",
    "bo.codes.revoke_confirm": "Yes, revoke",
    "bo.codes.revoke_warn": "Revoke {code}? Nobody new can sign up with it, and it is never issued again. Everyone who already signed up keeps their account and QR.",
    "bo.status.active": "Active",
    "bo.status.revoked": "Revoked",
    "bo.status.expired": "Expired",
    "bo.status.full": "Full",
    "bo.status.personal": "personal",
    "bo.venues.hint": "Add a venue here, or by issuing its first code. Its name is what guests read on the tip screen. A venue can be deleted only while nobody works there and it has no tables or tips.",
    "bo.venues.add_title": "New venue",
    "bo.venues.add": "Add venue",
    "bo.venues.added": "Done: {venue}. Issue it a code so its people can sign up.",
    "bo.venues.issue": "Issue code",
    "bo.venues.delete": "Delete",
    "bo.venues.delete_confirm": "Yes, delete",
    "bo.venues.delete_warn": "Delete {name}? This cannot be undone.",
    "bo.venues.delete_warn_codes": "Delete {name}? Its unused codes ({codes}) stop working. This cannot be undone.",
    "bo.venues.deleted": "{name} deleted.",
    "bo.venues.name": "Name",
    "bo.venues.neighborhood": "Neighborhood",
    "bo.venues.codes": "Live codes",
    "bo.venues.staff": "Staff",
    "bo.venues.created": "Created",
    "bo.venues.edit": "Edit",
    "bo.venues.save": "Save",
    "bo.venues.protected": "Store review · managed by seedreview",
    "bo.venues.rename_note": "Guests see the new name from the next scan on, printed QR codes included.",
    "bo.staff.name": "Name",
    "bo.staff.email": "Email",
    "bo.staff.venue": "Venue",
    "bo.staff.code": "Code",
    "bo.staff.status": "Status",
    "bo.staff.signed_up": "Signed up",
    "bo.staff.all_venues": "All venues",
    "bo.staff.search": "Search name or email",
    "bo.staff.code_filter": "Code {code}",
    "bo.staff.code_unknown": "Signed up before the code was recorded",
    "bo.staff.count": "{n} people",
    "bo.staff.count_one": "1 person",
    "bo.staff.status.confirmed": "Confirmed",
    "bo.staff.status.pending": "Unconfirmed",
    "bo.staff.status.closed": "Account closed",
    "bo.err.code_shape": "A code is 6 characters from A–Z and 2–9, without O, 0, I or 1.",
    "bo.err.code_taken": "That code already opens another venue. Choose another.",
    "bo.err.code_revoked": "That code was revoked and is never issued again. Choose another.",
    "bo.err.code_personal": "That is one waiter's personal code. Choose another.",
    "bo.err.code_not_found": "No such code.",
    "bo.err.venue_required": "Type the venue's name.",
    "bo.err.venue_too_long": "The name is too long (80 characters at most).",
    "bo.err.neighborhood_too_long": "The neighborhood is too long (80 characters at most).",
    "bo.err.venue_protected": "That is the store reviewers' venue; seedreview manages it.",
    "bo.err.venue_name_taken": "Another venue already has that name.",
    "bo.err.venue_ambiguous": "Several venues have that name; rename one first.",
    "bo.err.venue_not_found": "That venue no longer exists.",
    "bo.err.max_uses_negative": "The maximum cannot be negative.",
    "bo.err.venue_in_use": "This venue has people, tables or tips, so it cannot be deleted. Rename it or revoke its codes instead.",
  },
  ru: {
    "app.title": "Панель Mana",
    "login.email": "Эл. почта",
    "login.password": "Пароль",
    "login.submit": "Войти",
    "login.error": "Неверная почта или пароль.",
    "nav.recipients": "Получатели",
    "nav.tip_pages": "Страницы чаевых",
    "nav.payouts": "Выплаты",
    "nav.gamification": "Геймификация",
    "nav.stats": "Сводка",
    "nav.logout": "Выйти",
    "recipients.name": "Имя",
    "recipients.kind": "Тип",
    "recipients.add": "Добавить получателя",
    "recipients.id": "ID",
    "tippages.recipient": "ID получателя",
    "tippages.currency": "Валюта",
    "tippages.presets": "Суммы (через запятую, в минорных единицах)",
    "tippages.greeting_es": "Приветствие (испанский)",
    "tippages.greeting_en": "Приветствие (английский)",
    "tippages.greeting_ru": "Приветствие (русский)",
    "tippages.add": "Создать страницу",
    "tippages.links": "Ссылки / QR",
    "tippages.slug": "Адрес",
    "tippages.presets_short": "Суммы",
    "payouts.recipient": "ID получателя",
    "payouts.currency": "Валюта",
    "payouts.trigger": "Выплатить баланс",
    "payouts.none": "Нет баланса к выплате.",
    "game.badge_code": "Код бейджа",
    "game.metric": "Метрика",
    "game.threshold": "Порог",
    "game.add_badge": "Создать бейдж",
    "stats.tips": "Чаевые",
    "stats.outstanding": "Балансы к выплате",
    "stats.count": "Кол-во",
    "stats.gross": "Брутто",
    "stats.fees": "Комиссии",
    "common.create": "Создать",
    "common.error": "Произошла ошибка.",
    "common.empty": "Пока пусто.",
    "login.too_many": "Слишком много попыток. Подождите несколько минут.",
    "common.cancel": "Отмена",
    "bo.title": "Бэкофис Mana",
    "bo.nav.codes": "Коды",
    "bo.nav.venues": "Заведения",
    "bo.nav.staff": "Зарегистрированные",
    "bo.issue.title": "Выдать код приглашения",
    "bo.issue.venue": "Заведение",
    "bo.issue.new_venue": "— Новое заведение —",
    "bo.issue.venue_name": "Название заведения",
    "bo.issue.neighborhood": "Район",
    "bo.issue.code": "Код",
    "bo.issue.code_placeholder": "Пусто — случайный",
    "bo.issue.code_hint": "6 символов из A–Z и 2–9, без O, 0, I и 1: экран регистрации их выкидывает.",
    "bo.issue.max_uses": "Макс. регистраций",
    "bo.issue.no_limit": "Без лимита",
    "bo.issue.submit": "Выдать код",
    "bo.issue.venue_created": "(новое заведение)",
    "bo.issue.existing": "{code} уже открывает {venue} — ничего не изменилось.",
    "bo.issue.tell": "Давайте код только тем, кто там работает: гости увидят «{name}» на экране чаевых.",
    "bo.issue.hood_ignored": "Заведение уже было с районом «{hood}» — оставлено как есть.",
    "bo.codes.code": "Код",
    "bo.codes.venue": "Заведение",
    "bo.codes.signups": "Регистраций",
    "bo.codes.status": "Статус",
    "bo.codes.issued": "Выдан",
    "bo.codes.empty": "Кодов пока нет. Выдайте первый выше.",
    "bo.codes.show_staff": "Кто зарегистрировался по этому коду",
    "bo.codes.revoke": "Отозвать",
    "bo.codes.revoke_confirm": "Да, отозвать",
    "bo.codes.revoke_warn": "Отозвать {code}? По нему больше никто не зарегистрируется, и повторно он не выдаётся никогда. Кто уже зарегистрировался, сохранит аккаунт и QR.",
    "bo.status.active": "Действует",
    "bo.status.revoked": "Отозван",
    "bo.status.expired": "Истёк",
    "bo.status.full": "Исчерпан",
    "bo.status.personal": "личный",
    "bo.venues.hint": "Добавьте заведение здесь или при выдаче первого кода. Его название гости видят на экране чаевых. Удалить заведение можно, только пока там никто не работает и нет столов и чаевых.",
    "bo.venues.add_title": "Новое заведение",
    "bo.venues.add": "Добавить",
    "bo.venues.added": "Готово: {venue}. Выдайте ему код, чтобы сотрудники могли зарегистрироваться.",
    "bo.venues.issue": "Выдать код",
    "bo.venues.delete": "Удалить",
    "bo.venues.delete_confirm": "Да, удалить",
    "bo.venues.delete_warn": "Удалить {name}? Это нельзя отменить.",
    "bo.venues.delete_warn_codes": "Удалить {name}? Его неиспользованные коды ({codes}) перестанут работать. Это нельзя отменить.",
    "bo.venues.deleted": "{name} удалено.",
    "bo.venues.name": "Название",
    "bo.venues.neighborhood": "Район",
    "bo.venues.codes": "Действующих кодов",
    "bo.venues.staff": "Официантов",
    "bo.venues.created": "Создано",
    "bo.venues.edit": "Изменить",
    "bo.venues.save": "Сохранить",
    "bo.venues.protected": "Ревью сторов · управляет seedreview",
    "bo.venues.rename_note": "Гости увидят новое название со следующего сканирования, в том числе на уже напечатанных QR.",
    "bo.staff.name": "Имя",
    "bo.staff.email": "Почта",
    "bo.staff.venue": "Заведение",
    "bo.staff.code": "Код",
    "bo.staff.status": "Статус",
    "bo.staff.signed_up": "Регистрация",
    "bo.staff.all_venues": "Все заведения",
    "bo.staff.search": "Поиск по имени или почте",
    "bo.staff.code_filter": "Код {code}",
    "bo.staff.code_unknown": "Зарегистрирован до того, как код начали сохранять",
    "bo.staff.count": "{n} чел.",
    "bo.staff.count_one": "1 чел.",
    "bo.staff.status.confirmed": "Подтверждён",
    "bo.staff.status.pending": "Не подтверждён",
    "bo.staff.status.closed": "Аккаунт закрыт",
    "bo.err.code_shape": "Код — 6 символов из A–Z и 2–9, без O, 0, I и 1.",
    "bo.err.code_taken": "Этот код уже открывает другое заведение. Выберите другой.",
    "bo.err.code_revoked": "Этот код отозван и повторно не выдаётся. Выберите другой.",
    "bo.err.code_personal": "Это личный код одного официанта. Выберите другой.",
    "bo.err.code_not_found": "Такого кода нет.",
    "bo.err.venue_required": "Введите название заведения.",
    "bo.err.venue_too_long": "Слишком длинное название (не больше 80 символов).",
    "bo.err.neighborhood_too_long": "Слишком длинный район (не больше 80 символов).",
    "bo.err.venue_protected": "Это заведение ревьюеров сторов, им управляет seedreview.",
    "bo.err.venue_name_taken": "Заведение с таким названием уже есть.",
    "bo.err.venue_ambiguous": "С таким названием несколько заведений; сначала переименуйте одно.",
    "bo.err.venue_not_found": "Такого заведения больше нет.",
    "bo.err.max_uses_negative": "Максимум не может быть отрицательным.",
    "bo.err.venue_in_use": "В заведении есть люди, столы или чаевые — удалить его нельзя. Переименуйте его или отзовите коды.",
  },
};

/** The locale a Mexican venue gets unless it asks for something else. */
export const DEFAULT_LOCALE = "es-MX";

/** What each locale is called, in its own language. */
export const localeLabels: Record<string, string> = {
  "es-MX": "Español",
  en: "English",
  ru: "Русский",
};

const STORAGE_KEY = "mana_dash_locale";

/**
 * Match a browser tag to a catalog we ship.
 *
 * Region tags matter here: `es-MX` has to find the catalog exactly, and every
 * other Spanish — `es`, `es-ES`, `es-AR` — lands on it too, because Mexican
 * Spanish is the only Spanish this product maintains. Shipping a second,
 * unmaintained "neutral Spanish" would be a translation nobody proofreads.
 */
function matchLocale(tag: string | null | undefined): string | null {
  if (!tag) return null;
  const wanted = tag.toLowerCase();
  for (const known of Object.keys(catalogs)) {
    if (known.toLowerCase() === wanted) return known;
  }
  const base = wanted.split("-")[0];
  if (base === "es") return DEFAULT_LOCALE;
  for (const known of Object.keys(catalogs)) {
    if (known.toLowerCase().split("-")[0] === base) return known;
  }
  return null;
}

export function resolveLocale(): string {
  const candidates = [localStorage.getItem(STORAGE_KEY), ...(navigator.languages ?? [navigator.language])];
  for (const cand of candidates) {
    const hit = matchLocale(cand);
    if (hit) return hit;
  }
  return DEFAULT_LOCALE;
}

/** Remember the manager's choice so it survives a reload. */
export function storeLocale(locale: string): void {
  localStorage.setItem(STORAGE_KEY, locale);
}

export function makeT(locale: string) {
  const cat = catalogs[locale] ?? catalogs[DEFAULT_LOCALE];
  // Falling back through the default rather than straight to the key means a
  // catalog that is missing a string shows Spanish, not `stats.gross`.
  return (key: string): string => cat[key] ?? catalogs[DEFAULT_LOCALE][key] ?? key;
}

export type T = ReturnType<typeof makeT>;
export const locales = Object.keys(catalogs);
